'use client'
import { useMemo, type FC } from 'react'
import styled, { css } from 'styled-components'
import { motion, Variants } from 'motion/react'
import { useDeviceType } from '@/hooks'
import { designGrid, getTransition, mq, presets, rgbAlpha, vw } from '@/styles'
import { Img, BgHover } from '@/components'

type Props = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getImages = (images: any[], mainType: string, isMobile: boolean) =>
  images.filter(Boolean).map((img, idx) => {
    return (
      <motion.div
        key={idx}
        className={`image-wrapper-${
          mainType === 'media' ? 'center' : img?.position?.toLowerCase() || 'center'
        }`}
        variants={IMAGE_VARIANTS}
        animate='active'
        initial='inactive'
        custom={isMobile}
      >
        <Img lazy={false} media={img} unoptimized={true}/>
      </motion.div>
    )
  })

export const HeaderHome: FC<Props> = ({ data }) => {
  const {
    title,
    subtitle,
    link,
    leftImage,
    centerImage,
    rightImage,
    textPosition,
    textAlign,
    media,
		mobileImage,
    mainType,
    textValign,
    titleSize,
    subtitleSize,
  } = data

  const images = useMemo(
    () =>
      mainType === 'product' ? [leftImage, centerImage, rightImage] : [{...media, mobile: mobileImage?.mobile ?? media.mobile}],
    [mainType, leftImage, centerImage, rightImage, media, mobileImage]
  )
  const { isMobile } = useDeviceType()
  const ImageList = useMemo(
    () => getImages(images, mainType, isMobile),
    [images, mainType, isMobile]
  )
  const filteredImages = useMemo(() => images.filter(Boolean), [images])
  const singleImage = filteredImages.length === 1

  return (
    <Header data-length={filteredImages.length}>
      {ImageList}
      <TextWrapper
        className={`text-wrapper ${textPosition?.toLowerCase() || 'center'}`}
        $title_size={titleSize}
        $subtitle_size={subtitleSize}
        $text_valign={textValign}
      >
        <motion.h2
          custom={singleImage}
          variants={text_variants_title}
          animate='active'
          initial='inactive'
          className={textAlign?.toLowerCase()}
        >
          {title}
        </motion.h2>
        <motion.p
          custom={singleImage}
          variants={text_variants_subtitle}
          animate='active'
          initial='inactive'
          className={textAlign?.toLowerCase()}
        >
          {subtitle}
        </motion.p>
        {link?.path && link?.title && (
          <LinkStyled
            href={link.path}
            variants={text_variants_button}
            animate='active'
            initial='inactive'
            className={textAlign?.toLowerCase()}
          >
            <BgHover
              $bgColor={'--color-gold'}
              $bgColorHover={'--color-gold'}
              $outline={false}
            />
            <span>{link.title}</span>
          </LinkStyled>
        )}
      </TextWrapper>
    </Header>
  )
}

const Header = styled.header`
  ${designGrid({})}
  place-items: center;
  overflow-x: hidden;

  ${mq.greaterThan('container')} {
    margin: 0 auto;
    max-width: var(--page-container-max-width);
  }

  div[class*='image-wrapper'] {
    grid-column: 1 / span 6;
    grid-row: 1 / span 1;
    position: relative;

    ${mq.greaterThan('tablet')} {
      grid-column: 1 / span 12;
    }

    &:after {
      background-color: ${rgbAlpha('--color-black', 0.2)};
      bottom: 0;
      content: '';
      height: 100%;
      left: 0;
      pointer-events: none;
      position: absolute;
      right: 0;
      top: 0;
      user-select: none;
      width: 100%;
    }
  }

  div[class*='image-wrapper'] > img {
    user-select: none;
    pointer-events: none;
    filter: drop-shadow(8px 13px 1px var(--color-shadow));
    height: ${vw(447, 'mobile')};

    ${mq.greaterThan('tablet')} {
      height: 100%;
    }
  }

  &[data-length='1'] {
    margin-bottom: ${vw(90, 'mobile')};
    color: var(--color-white);

    div[class*='image-wrapper'] {
      transform: none;
      width: 100%;
      z-index: -1;

      &.figure--video {
        grid-column: 2 / span 6;
        filter: none;
        z-index: -1;
        height: ${vw(447, 'mobile')};
        overflow: hidden;

        & + [class*='text-wrapper'] {
          grid-column: 3 / span 4;
          margin-top: 0;
        }
      }

      &.image-wrapper-center {
        z-index: -1;
        height: ${vw(447, 'mobile')};

        > img {
          filter: none;
        }

				picture,
        img {
          object-fit: cover;
          height: 100%;
        }

        & + [class*='text-wrapper'] {
          grid-column: 2 / span 4;
          margin-top: 0;
          padding-bottom: 0;
        }
      }
    }
  }

  &[data-length='2'] {
    div[class*='image-wrapper']:nth-of-type(1) {
      grid-column: 1 / span 4;
    }

    div[class*='image-wrapper']:nth-of-type(2) {
      grid-column: 5 / span 4;
    }
  }

  &[data-length='3'] {
    div[class*='image-wrapper']:nth-of-type(1) {
      grid-column: 1 / span 4;
    }

    div[class*='image-wrapper']:nth-of-type(2) {
      grid-column: 3 / span 4;
    }

    div[class*='image-wrapper']:nth-of-type(3) {
      grid-column: 5 / span 4;
    }
  }

  ${mq.greaterThan('tablet')} {
    height: ${vw(750, 'desktop')};
    // margin-bottom: ${vw(50, 'desktop')};
    overflow-x: unset;

    div[class*='image-wrapper'] {
      width: ${vw(720, 'desktop')};
      height: ${vw(750, 'desktop')};
    }

    &[data-length] div[class*='image-wrapper'] {
      > img {
        filter: drop-shadow(25px 40px 4px var(--color-shadow));
      }

      &.image-wrapper-left {
        grid-column: 1 / span 6;
        margin: 0;
      }
      &.image-wrapper-center {
        grid-column: 4 / span 6;
      }
      &.image-wrapper-right {
        grid-column: 7 / span 6;
      }
    }

    &[data-length='1'] {
      margin-bottom: ${vw(70, 'desktop')};

      div[class*='image-wrapper'] {
        &.image-wrapper-center {
          grid-column: 1 / span 12;
          transform: none;
          width: 100%;
          height: inherit;

          & + [class*='text-wrapper'] {
            &.text-wrapper-left {
              grid-column: 2 / span 5;
            }
            &.text-wrapper-center {
              grid-column: 3 / span 8;
            }
            &.text-wrapper-right {
              grid-column: 7 / span 5;
            }
          }
        }
      }

      img {
        &.figure--video {
          grid-column: 1 / span 12;
          height: ${vw(715, 'desktop')};

          & + [class*='text-wrapper'] {
            &.text-wrapper-left {
              grid-column: 2 / span 5;
            }
            &.text-wrapper-center {
              grid-column: 3 / span 8;
            }
            &.text-wrapper-right {
              grid-column: 7 / span 5;
            }
          }
        }
      }
    }
  }

  ${mq.greaterThan('desktop')} {
    height: 750px;

    div[class*='image-wrapper'] {
      width: 720px;
      height: 750px;
    }

    &[data-length='1'] {
      margin-bottom: 70px;

      img {
        &.figure--video {
          height: 715px;
        }
      }
    }
  }

  ${mq.greaterThan('container')} {
    margin: 0 auto;
    max-width: var(--page-container-max-width);
  }


	
`

type TextWrapperProps = {
  $text_align?: 'left' | 'center' | 'right'
  $title_size?: string
  $subtitle_size?: string
  $text_valign?: 'top' | 'middle' | 'bottom'
}

const TextWrapper = styled.div<TextWrapperProps>`
  grid-column: 3 / span 4;
  grid-row: 1 / span 1;
  padding-bottom: ${vw(80, 'mobile')}; // fix for animations
  width: 100%;

  ${({ $text_valign }) =>
    $text_valign === 'top'
      ? css`
          padding-top: ${vw(20, 'mobile')};
          align-self: start;
        `
      : $text_valign === 'bottom'
        ? css`
          padding-bottom: ${vw(30, 'mobile')};
          align-self: end;
        `
        : css`
          align-self: center;
        `};

  ${mq.greaterThan('nexus7')} {
    margin-top: -${vw(40, 'tablet')};
    padding-bottom: ${vw(80, 'tablet')}; // fix for animations
    ${({ $text_valign }) =>
    $text_valign === 'top'
      ? css`
            padding-top: ${vw(20, 'tablet')};
            align-self: start;
          `
      : $text_valign === 'bottom'
        ? css`
            padding-bottom: ${vw(30, 'tablet')};
            align-self: end;
          `
        : css`
            align-self: center;
          `}
  }

  ${mq.greaterThan('tablet')} {
    grid-area: 1 / 5 / span 1 / span 4;
    margin-top: 0;
    padding-bottom: 0;
    ${({ $text_valign }) =>
    $text_valign === 'top'
      ? css`
            padding-top: ${vw(60, 'desktop')};
            align-self: start;
          `
      : $text_valign === 'bottom'
        ? css`
            padding-bottom: ${vw(70, 'desktop')};
            align-self: end;
          `
        : css`
            align-self: center;
          `};
  }

  ${mq.greaterThan('desktop')} {
    ${({ $text_valign }) =>
    $text_valign === 'top'
      ? css`
            padding-top: 60px;
            align-self: start;
          `
      : $text_valign === 'bottom'
        ? css`
            padding-bottom: 70px;
            align-self: end;
          `
        : css`
            align-self: center;
          `};
  }


  h2,
  p {
    text-shadow: 1px 1px 2px ${rgbAlpha('--color-black', .15)};
  }

  h2 {
    ${({ $title_size }) => $title_size === 'xl' ? presets.font141() : $title_size === 'l' ? presets.font113() : $title_size === 'm' ? presets.font93_1 : presets.font53};
  }

  p {
    ${({ $subtitle_size }) =>
    $subtitle_size === 'l'
      ? css`
            ${presets.font30_1}
          `
      : $subtitle_size === 'm'
        ? css`
            ${presets.font30_1}
          `
        : presets.font15_1};
    margin: ${vw(15, 'mobile')} 0 ${vw(20, 'mobile')};

    ${mq.greaterThan('tablet')} {
      ${({ $subtitle_size }) => $subtitle_size === 'l' ? css` ${presets.font30_1} `
      : $subtitle_size === 'm'
        ? css`
            ${presets.font26}
            letter-spacing: -1.5px;
          `
        : presets.font26_1};
    }
  }

  .left,
  .center,
  .right {
    text-align: center;
  }

  .left {
    ${mq.greaterThan('tablet')} {
      text-align: left;
    }
  }

  .right {
    ${mq.greaterThan('tablet')} {
      text-align: right;
    }
  }

  .text-wrapper {
    &.left {
      ${mq.greaterThan('tablet')} {
        grid-column: 2 / span 5;
      }

      p {
        ${mq.greaterThan('tablet')} {
          &.left {
            margin: ${vw(15, 'desktop')} calc(100vw / 12) ${vw(30, 'desktop')} 0;
          }
          &.center {
            margin: ${vw(15, 'desktop')} calc(100vw / 24) ${vw(30, 'desktop')};
          }
          &.right {
            margin: ${vw(15, 'desktop')} 0 ${vw(30, 'desktop')} calc(100vw / 12);
          }
        }

        ${mq.greaterThan('desktop')} {
          &.left {
            margin: 15px calc(100vw / 12) 30px 0;
          }
          &.center {
            margin: 15px calc(100vw / 24) 30px;
          }
          &.right {
            margin: 15px 0 30px calc(100vw / 12);
          }
        }
      }
    }


    &.center {
      ${mq.greaterThan('tablet')} {
        grid-column: 3 / span 8;
      }

      p {
        ${mq.greaterThan('tablet')} {
          &.left {
            margin: ${vw(15, 'desktop')} calc(100vw / 12 * 4) ${vw(30, 'desktop')} 0;
          }
          &.center {
            margin: ${vw(15, 'desktop')} calc(100vw / 6) ${vw(30, 'desktop')};
          }
          &.right {
            margin: ${vw(15, 'desktop')} 0 ${vw(30, 'desktop')} calc(100vw / 12 * 4);
          }
        }

        ${mq.greaterThan('desktop')} {
          &.left {
            margin: 15px calc(100vw / 12 * 4) 30px 0;
          }
          &.center {
            margin: 15px calc(100vw / 6) 30px;
          }
          &.right {
            margin: 15px 0 30px calc(100vw / 12 * 4);
          }
        }
      }
    }

    &.right {
      ${mq.greaterThan('tablet')} {
        grid-column: 7 / span 5;
      }

      p {
        ${mq.greaterThan('tablet')} {
          &.left {
            margin: ${vw(15, 'desktop')} calc(100vw / 12) ${vw(30, 'desktop')} 0;
          }
          &.center {
            margin: ${vw(15, 'desktop')} calc(100vw / 24) ${vw(30, 'desktop')};
          }
          &.right {
            margin: ${vw(15, 'desktop')} 0 ${vw(30, 'desktop')} calc(100vw / 12);
          }
        }

        ${mq.greaterThan('desktop')} {
          &.left {
            margin: 15px calc(100vw / 12) 30px 0;
          }
          &.center {
            margin: 15px calc(100vw / 24) 30px;
          }
          &.right {
            margin: 15px 0 30px calc(100vw / 12);
          }
        }
      }
    }
  }

  a {
    &.left {
      ${mq.greaterThan('tablet')} {
        margin: 0 auto 0 0;
      }
    }

    &.right {
      ${mq.greaterThan('tablet')} {
        margin: 0 0 0 auto;
      }
    }
  }
`

const LinkStyled = styled(motion.a)`
  ${presets.font15}
  align-items: center;
  background-color: var('--color-gold');
  border-radius: ${vw(30, 'mobile')};
  color: var('--color-white');
  display: flex;
  font-family: var(--font-graphik);
  justify-content: center;
  letter-spacing: 0.13px;
  margin: 0 auto;
  padding: ${vw(17, 'mobile')} ${vw(30, 'mobile')};
  position: relative;
  width: max-content;
  z-index: auto;

  ${mq.greaterThan('nexus7')} {
    border-radius: ${vw(30, 'tablet')};
    padding: ${vw(17, 'tablet')} ${vw(30, 'tablet')};
  }

  ${mq.greaterThan('tablet')} {
    ${presets.font18_4}
    border-radius:${vw(30, 'desktop')};
    letter-spacing: 0;
    padding: ${vw(19, 'desktop')} ${vw(43, 'desktop')};
  }

  ${mq.greaterThan('desktop')} {
    border-radius: 30px;
    padding: 19px 43px;
  }

  &[type='submit'] {
    ${mq.greaterThan('tablet')} {
      font-weight: 500;
    }
  }

  &[disabled] {
    cursor: not-allowed;
    opacity: 0.4;
    pointer-events: none;
  }

  span {
    margin: 0 !important;
    padding: 0;
    z-index: 2;
  }

  &:hover {
    > div {
      ${mq.greaterThan('tablet')} {
        transform: scale3d(1.1, 1.2, 1);
      }
    }
  }
`

const text_variants_title = {
  inactive: {
    opacity: 0,
    y: '30px',
  },
  active: {
    opacity: 1,
    y: '0px',
    transition: {
      ...getTransition(0.5),
      delay: .6
    },
  },
}

const text_variants_subtitle = {
  inactive: {
    opacity: 0,
    y: '30px',
  },
  active: {
    opacity: 1,
    y: '0px',
    transition: {
      ...getTransition(0.5),
      delay: .7
    },
  }
}

const text_variants_button = {
  inactive: {
    opacity: 0,
    y: '30px',
  },
  active: {
    opacity: 1,
    y: '0px',
    transition: {
      ...getTransition(0.5),
      delay: .8
    },
  }
}

const IMAGE_VARIANTS:Variants = {
  inactive: {
    opacity: 0,
    y: '-8px'
  },
  active: {
    opacity: 1,
    y: 0,
    transition: {
      ...getTransition(1),
      delay: 0,
    }
  }
}
